import React, { useCallback, useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LABEL = { archaka_subscription: "అర్చక నెలవారీ సభ్యత్వం", archaka_unlock: "అర్చక అన్‌లాక్", temple_unlock: "దేవాలయ అన్‌లాక్", credit_pack: "క్రెడిట్ ప్యాక్" };
const STATUS = { under_review: ["పరిశీలనలో", "bg-amber-100 text-amber-800"], paid: ["ఆమోదించారు", "bg-green-100 text-green-800"], rejected: ["తిరస్కరించారు", "bg-red-100 text-red-800"] };

export default function AdminPayments({ onChange }) {
  const [filter, setFilter] = useState("under_review");
  const [list, setList] = useState([]);

  const load = useCallback(() => { api.get(`/admin/payments?status=${filter}`).then(({ data }) => setList(data)).catch(() => {}); }, [filter]);
  useEffect(() => { load(); }, [load]);

  const decide = async (tx, decision) => {
    let reason = "";
    if (decision === "rejected") { reason = window.prompt("తిరస్కరణ కారణం:"); if (reason === null) return; }
    else if (!window.confirm(`₹${tx.amount} చెల్లింపు అందిందని నిర్ధారించారా?`)) return;
    try { await api.patch(`/admin/payments/${tx.txn_id}`, { decision, reason }); toast.success(decision === "approved" ? "ఆమోదించబడింది ✅" : "తిరస్కరించబడింది"); load(); onChange && onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "లోపం"); }
  };

  return (
    <div className="space-y-3" data-testid="admin-payments">
      <div className="flex gap-2">
        {[["under_review", "పరిశీలనలో"], ["paid", "ఆమోదించినవి"], ["rejected", "తిరస్కరించినవి"], ["all", "అన్నీ"]].map(([k, l]) => (
          <Button key={k} size="sm" data-testid={`pay-filter-${k}`} variant={filter === k ? "default" : "outline"} onClick={() => setFilter(k)} className={`font-telugu ${filter === k ? "bg-saffron" : ""}`}>{l}</Button>
        ))}
      </div>
      {list.length === 0 && <p className="text-sm text-muted-foreground font-telugu">చెల్లింపులు లేవు</p>}
      {list.map((tx) => (
        <Card key={tx.txn_id} className="p-4 bg-surface gold-border flex flex-col sm:flex-row gap-4" data-testid={`admin-pay-${tx.txn_id}`}>
          <a href={`${BACKEND_URL}${tx.screenshot_url}`} target="_blank" rel="noreferrer" data-testid={`admin-pay-shot-${tx.txn_id}`}>
            <img src={`${BACKEND_URL}${tx.screenshot_url}`} alt="screenshot" className="w-28 h-40 object-cover rounded-lg border border-goldline/40" />
          </a>
          <div className="flex-1 text-sm font-telugu space-y-1">
            <div className="flex items-center gap-2"><span className="text-xl font-bold text-saffron">₹{tx.amount}</span><Badge className={STATUS[tx.payment_status]?.[1]}>{STATUS[tx.payment_status]?.[0] || tx.payment_status}</Badge></div>
            <p className="text-maroon">{LABEL[tx.item_type]}{tx.item_type === "credit_pack" ? ` (${tx.package === "standard" ? 5 : 10})` : ""}</p>
            <p>{tx.user?.name} · {tx.user?.mobile || ""} · {tx.user?.unique_id || ""}</p>
            <p className="text-xs text-muted-foreground">ID: {tx.txn_id}{tx.utr ? ` · UTR: ${tx.utr}` : ""} · {new Date(tx.created_at).toLocaleString("te-IN")}</p>
            {tx.reject_reason && <p className="text-xs text-red-700">కారణం: {tx.reject_reason}</p>}
          </div>
          {tx.payment_status === "under_review" && (
            <div className="flex sm:flex-col gap-2">
              <Button size="sm" data-testid={`admin-pay-approve-${tx.txn_id}`} onClick={() => decide(tx, "approved")} className="bg-green-600 hover:bg-green-700 font-telugu"><Check className="w-4 h-4 mr-1" />ఆమోదించు</Button>
              <Button size="sm" variant="outline" data-testid={`admin-pay-reject-${tx.txn_id}`} onClick={() => decide(tx, "rejected")} className="border-red-300 text-red-700 font-telugu"><X className="w-4 h-4 mr-1" />తిరస్కరించు</Button>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
